import { useCallback, useRef, useState } from 'react';
import { Camera, UploadCloud } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { uploadFile } from '@/api/files';
import { cn } from '@/lib/cn';
import { useUIStore } from '@/stores/ui';
import { apiError } from '@/lib/api';

interface FileUploaderProps {
  folderId: string;
  folderName?: string;
  disabled?: boolean;
}

const IMAGE_MAX_BYTES = 4 * 1024 * 1024; // 4 MB — only compress above this

async function compressImageToJpeg(file: File): Promise<File> {
  // Under 4 MB: upload original, no quality loss
  if (file.size <= IMAGE_MAX_BYTES) return file;

  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      // Keep up to 4K resolution; only downscale if truly enormous
      const MAX = 3840;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width >= height) {
          height = Math.round((height * MAX) / width);
          width = MAX;
        } else {
          width = Math.round((width * MAX) / height);
          height = MAX;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(objectUrl);
      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return; }
          const baseName = file.name.replace(/\.[^.]+$/, '');
          resolve(new File([blob], `${baseName}.jpg`, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          }));
        },
        'image/jpeg',
        0.92,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file); };
    img.src = objectUrl;
  });
}

export function FileUploader({ folderId, folderName = '', disabled }: FileUploaderProps) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const push = useUIStore((s) => s.pushToast);
  const startUpload = useUIStore((s) => s.startUpload);
  const endUpload = useUIStore((s) => s.endUpload);
  const addUploadItem = useUIStore((s) => s.addUploadItem);
  const updateUploadItem = useUIStore((s) => s.updateUploadItem);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;

      const uploadOne = async (f: File) => {
        const file = f.type.startsWith('image/') ? await compressImageToJpeg(f) : f;
        const itemId = addUploadItem({ fileName: file.name, folderId, folderName, status: 'waiting', percent: 0 });
        startUpload();
        try {
          await uploadFile(folderId, file, (p) => {
            updateUploadItem(itemId, {
              status: p.status === 'done' ? 'done' : p.status === 'error' ? 'error' : 'uploading',
              percent: p.percent,
            });
          });
          qc.invalidateQueries({ queryKey: ['files', folderId] });
        } catch (e) {
          updateUploadItem(itemId, { status: 'error', error: apiError(e) });
          push({ title: `Upload failed: ${file.name}`, body: apiError(e), variant: 'error' });
        } finally {
          endUpload();
        }
      };

      // Bounded concurrency: a big drop (dozens/hundreds of files) fires 2 API
      // calls each. Firing them all at once floods the token-refresh path and
      // overwhelms the backend; a small worker pool keeps it steady and lets one
      // shared token refresh cover the whole batch.
      const CONCURRENCY = 4;
      let cursor = 0;
      const worker = async () => {
        while (cursor < list.length) {
          const f = list[cursor++];
          await uploadOne(f);
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, list.length) }, worker));
    },
    [folderId, folderName, qc, push, startUpload, endUpload, addUploadItem, updateUploadItem],
  );

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {/* Main drop zone */}
        <div
          onDragOver={(e) => {
            if (disabled) return;
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            if (disabled) return;
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
          }}
          onClick={() => !disabled && inputRef.current?.click()}
          role="button"
          tabIndex={0}
          aria-disabled={disabled}
          className={cn(
            'flex-1 border-2 border-dashed p-5 text-center transition-colors cursor-pointer',
            dragOver ? 'border-[#0410BD] bg-[#EFF0FF]' : 'border-[#E3E3F1] bg-white',
            disabled && 'opacity-60 pointer-events-none',
          )}
        >
          <UploadCloud className="mx-auto mb-2 text-[#0410BD]" size={26} />
          <p className="text-sm font-medium text-slate-800">
            Drop files to upload, or click to browse
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Files are stored securely and versioned automatically.
          </p>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        {/* Camera button — mobile only */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && cameraRef.current?.click()}
          title="Take a photo"
          className={cn(
            'md:hidden flex flex-col items-center justify-center gap-1.5 px-4 border-2 border-dashed border-[#E3E3F1] bg-white text-[#676687] hover:border-[#0410BD] hover:text-[#0410BD] hover:bg-[#EFF0FF] transition-colors cursor-pointer',
            disabled && 'opacity-60 pointer-events-none',
          )}
        >
          <Camera size={22} />
          <span className="text-[11px] font-medium whitespace-nowrap">Take photo</span>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              if (e.target.files) handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </button>
      </div>

    </div>
  );
}
